import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingStateProps {
  /** Message to display while loading */
  message?: string;
  /** Additional context about what's being loaded */
  context?: string;
  /** Size of the spinner: 'sm' | 'md' | 'lg' */
  size?: 'sm' | 'md' | 'lg';
  /** Additional className for the container */
  className?: string;
  /** Whether to show in a full-height container */
  fullHeight?: boolean;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

const textSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

/**
 * A contextual loading state component that shows what's being loaded.
 * Better UX than a generic spinner because users know what to expect.
 *
 * @example
 * <LoadingState message="Cargando facturas" context="Obteniendo datos del servidor..." />
 *
 * @example
 * <LoadingState message="Guardando" size="sm" />
 */
export function LoadingState({
  message = 'Cargando...',
  context,
  size = 'md',
  className,
  fullHeight = true,
}: LoadingStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        fullHeight && 'h-64',
        className
      )}
    >
      <Loader2
        className={cn('animate-spin text-primary', sizeClasses[size])}
      />
      <div className="text-center">
        <p className={cn('font-medium text-foreground', textSizeClasses[size])}>
          {message}
        </p>
        {context && (
          <p className={cn('text-muted-foreground mt-1', size === 'lg' ? 'text-sm' : 'text-xs')}>
            {context}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A simple inline loading indicator for buttons or small areas.
 */
export function InlineLoader({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin', className)} />;
}

/**
 * Loading state for table cells (spans full row).
 */
export function TableLoadingState({
  colSpan,
  message = 'Cargando datos...',
}: {
  colSpan: number;
  message?: string;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="h-24 text-center">
        <LoadingState message={message} fullHeight={false} />
      </td>
    </tr>
  );
}

export default LoadingState;
